import { supabase } from '../lib/supabase';

// Helper utilities for validation and error handling
const validationUtils = {
  isValidUUID(id) {
    if (!id) return false;
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex?.test(id);
  }
};

const errorHandler = {
  async handleAsync(fn, context) {
    try {
      return await fn();
    } catch (error) {
      console.error(`Error in ${context}:`, error);
      throw error;
    }
  }
};

export const boothEnhancementsService = {
  // ===== PHOTO GALLERY =====
  async getBoothGallery(instituteId) {
    const { data, error } = await supabase?.from('booth_gallery')?.select('*')?.eq('institute_id', instituteId)?.order('display_order', { ascending: true });

    if (error) throw error;
    return data?.map(item => ({
      id: item?.id,
      instituteId: item?.institute_id,
      imageUrl: item?.image_url,
      caption: item?.caption,
      displayOrder: item?.display_order,
      isFeatured: item?.is_featured,
      createdAt: item?.created_at
    }));
  },

  async addGalleryImage(instituteId, imageData) {
    const { data, error } = await supabase?.from('booth_gallery')?.insert({
        institute_id: instituteId,
        image_url: imageData?.imageUrl,
        caption: imageData?.caption,
        display_order: imageData?.displayOrder || 0,
        is_featured: imageData?.isFeatured || false
      })?.select()?.single();

    if (error) throw error;
    return data;
  },

  async deleteGalleryImage(imageId) {
    const { error } = await supabase?.from('booth_gallery')?.delete()?.eq('id', imageId);

    if (error) throw error;
  },

  // ===== REVIEWS & RATINGS =====
  async getBoothReviews(instituteId) {
    const { data, error } = await supabase?.from('booth_reviews')?.select(`
        *,
        student:student_id(id, name, email)
      `)?.eq('institute_id', instituteId)?.eq('is_approved', true)?.order('created_at', { ascending: false });

    if (error) throw error;
    return data?.map(review => ({
      id: review?.id,
      instituteId: review?.institute_id,
      studentId: review?.student_id,
      rating: review?.rating,
      reviewText: review?.review_text,
      programName: review?.program_name,
      yearAttended: review?.year_attended,
      isVerified: review?.is_verified,
      createdAt: review?.created_at,
      student: review?.student
    }));
  },

  async submitReview(reviewData) {
    const { data, error } = await supabase?.from('booth_reviews')?.insert({
        institute_id: reviewData?.instituteId,
        student_id: reviewData?.studentId,
        rating: reviewData?.rating,
        review_text: reviewData?.reviewText,
        program_name: reviewData?.programName,
        year_attended: reviewData?.yearAttended
      })?.select()?.single();

    if (error) throw error;
    return data;
  },

  async updateReview(reviewId, updates) {
    const { data, error } = await supabase?.from('booth_reviews')?.update({
        rating: updates?.rating,
        review_text: updates?.reviewText,
        program_name: updates?.programName,
        year_attended: updates?.yearAttended,
        updated_at: new Date()?.toISOString()
      })?.eq('id', reviewId)?.select()?.single();

    if (error) throw error;
    return data;
  },

  async getAverageRating(instituteId) {
    const { data, error } = await supabase?.from('booth_reviews')?.select('rating')?.eq('institute_id', instituteId)?.eq('is_approved', true);

    if (error) throw error;
    
    if (!data || data?.length === 0) return { average: 0, count: 0 };
    
    const sum = data?.reduce((acc, review) => acc + review?.rating, 0);
    return {
      average: (sum / data?.length)?.toFixed(1),
      count: data?.length
    };
  },

  // ===== COUNSELOR BOOKINGS =====
  /**
   * Model B: Get available slots for a date using institute_availability table
   * Returns slots that still have counselor capacity remaining
   */
  async getAvailableSlots(instituteId, date) {
    if (!instituteId || !date) return [];

    // CRITICAL FIX: institute_availability.institute_id references users.id, not institute_profiles.id
    // Resolve institute_profiles.id → users.id first
    let instituteUserId = instituteId;
    const { data: profileData } = await supabase
      ?.from('institute_profiles')
      ?.select('user_id')
      ?.eq('id', instituteId)
      ?.single();
    if (profileData?.user_id) {
      instituteUserId = profileData?.user_id;
    }

    const dateObj = new Date(date);
    const dayOfWeek = dateObj?.getDay(); // 0=Sun … 6=Sat

    // Fetch institute's availability for this day of week
    const { data: availSlots, error: availError } = await supabase
      ?.from('institute_availability')
      ?.select('start_time, end_time, max_counselors')
      ?.eq('institute_id', instituteUserId)
      ?.eq('day_of_week', dayOfWeek)
      ?.eq('is_active', true)
      ?.order('start_time', { ascending: true });

    if (availError) throw availError;
    if (!availSlots || availSlots?.length === 0) return [];

    // Fetch booking counts per time for this date
    const { data: booked, error: bookedError } = await supabase
      ?.from('counselor_bookings')
      ?.select('booking_time')
      ?.eq('institute_id', instituteUserId)
      ?.eq('booking_date', date)
      ?.in('status', ['confirmed', 'pending']);

    if (bookedError) throw bookedError;

    // Count bookings per time slot
    const bookingCounts = {};
    (booked || [])?.forEach(b => {
      const t = (b?.booking_time || '')?.substring(0, 5);
      bookingCounts[t] = (bookingCounts?.[t] || 0) + 1;
    });

    // Return slots that still have capacity
    return availSlots
      ?.filter(slot => {
        const slotTime = (slot?.start_time || '')?.substring(0, 5);
        const maxCounselors = slot?.max_counselors ?? 1;
        return (bookingCounts?.[slotTime] || 0) < maxCounselors;
      })
      ?.map(slot => {
        const slotTime = (slot?.start_time || '')?.substring(0, 5);
        const maxCounselors = slot?.max_counselors ?? 1;
        const bookedCount = bookingCounts?.[slotTime] || 0;
        return {
          value: slotTime,
          label: formatSlotLabel(slot?.start_time, slot?.end_time),
          maxCounselors,
          bookedCount,
          remainingSpots: maxCounselors - bookedCount
        };
      });
  },

  /**
   * Model B: Create booking with auto-confirmed status
   */
  async createBooking(bookingData) {
    // CRITICAL FIX: counselor_bookings.institute_id references users.id, not institute_profiles.id
    // Resolve institute_profiles.id → users.id first
    let instituteUserId = bookingData?.instituteId;
    const { data: profileData } = await supabase
      ?.from('institute_profiles')
      ?.select('user_id')
      ?.eq('id', bookingData?.instituteId)
      ?.single();
    if (profileData?.user_id) {
      instituteUserId = profileData?.user_id;
    }

    const { data, error } = await supabase?.from('counselor_bookings')?.insert({
        institute_id: instituteUserId,
        student_id: bookingData?.studentId,
        booking_date: bookingData?.bookingDate,
        booking_time: bookingData?.bookingTime,
        duration_minutes: bookingData?.durationMinutes || 30,
        notes: bookingData?.notes,
        status: 'confirmed'  // Model B: auto-confirm
      })?.select()?.single();

    if (error) throw error;

    // Send notifications to both student and institute
    try {
      const { notificationService } = await import('./notificationService');

      // Fetch institute name for notification messages
      const { data: instituteUser } = await supabase
        ?.from('users')
        ?.select('name')
        ?.eq('id', instituteUserId)
        ?.single();

      // Fetch student name for institute notification
      const { data: studentUser } = await supabase
        ?.from('users')
        ?.select('name')
        ?.eq('id', bookingData?.studentId)
        ?.single();

      const instituteName = instituteUser?.name || 'Institute';
      const studentName = studentUser?.name || 'A student';
      const sessionDateStr = new Date(bookingData?.bookingDate)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      const [hours, minutes] = (bookingData?.bookingTime || '00:00')?.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      const timeStr = `${displayHour}:${minutes || '00'} ${ampm}`;

      // Notify student: booking confirmed
      await notificationService?.createInAppNotification({
        userId: bookingData?.studentId,
        title: 'Video Counseling Session Confirmed!',
        message: `Your session with ${instituteName} is confirmed for ${sessionDateStr} at ${timeStr}.`,
        category: 'session',
        metadata: { bookingId: data?.id, instituteId: instituteUserId, bookingDate: bookingData?.bookingDate, bookingTime: bookingData?.bookingTime },
        actionUrl: `/student-dashboard`,
        priority: 'high'
      });

      // Notify institute: new booking received — use resolved users.id so notification appears in institute dashboard
      await notificationService?.createInAppNotification({
        userId: instituteUserId,
        title: 'New Counseling Session Booked',
        message: `${studentName} has booked a video counseling session on ${sessionDateStr} at ${timeStr}.`,
        category: 'session',
        metadata: { bookingId: data?.id, studentId: bookingData?.studentId, bookingDate: bookingData?.bookingDate, bookingTime: bookingData?.bookingTime },
        actionUrl: `/institute-dashboard#sessions`,
        priority: 'high'
      });
    } catch (notifError) {
      console.error('Error sending booking notifications:', notifError);
    }

    return data;
  },

  async getStudentBookings(studentId) {
    const { data, error } = await supabase?.from('counselor_bookings')?.select(`
        *,
        institute:institute_id(id, name, email)
      `)?.eq('student_id', studentId)?.order('booking_date', { ascending: true });

    if (error) throw error;
    return data?.map(booking => ({
      id: booking?.id,
      instituteId: booking?.institute_id,
      studentId: booking?.student_id,
      bookingDate: booking?.booking_date,
      bookingTime: booking?.booking_time,
      durationMinutes: booking?.duration_minutes,
      status: booking?.status,
      meetingLink: booking?.meeting_link,
      notes: booking?.notes,
      institute: booking?.institute
    }));
  },

  async cancelBooking(bookingId) {
    const { data, error } = await supabase?.from('counselor_bookings')?.update({ status: 'cancelled', updated_at: new Date()?.toISOString() })?.eq('id', bookingId)?.select()?.single();

    if (error) throw error;
    return data;
  },

  // ===== DOWNLOADABLE RESOURCES =====
  async getBoothResources(instituteId) {
    return errorHandler?.handleAsync(async () => {
      if (!validationUtils?.isValidUUID(instituteId)) {
        throw new Error('Invalid institute ID');
      }

      const { data, error } = await supabase
        ?.from('booth_resources')
        ?.select('*')
        ?.eq('institute_id', instituteId)
        ?.eq('is_public', true)
        ?.order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    }, 'getBoothResources');
  },

  async getBoothData(instituteId) {
    return errorHandler?.handleAsync(async () => {
      if (!validationUtils?.isValidUUID(instituteId)) {
        throw new Error('Invalid institute ID');
      }

      const { data, error } = await supabase
        ?.from('virtual_booths')
        ?.select('*')
        ?.eq('institute_id', instituteId)
        ?.single();

      if (error && error?.code !== 'PGRST116') throw error;
      return data || { video_urls: [], brochure_urls: [], application_links: [], campus_tour_urls: [] };
    }, 'getBoothData');
  },

  async trackResourceDownload(resourceId) {
    const { error } = await supabase?.rpc('increment_resource_download', { p_resource_id: resourceId });

    if (error) throw error;
  },

  async addResource(resourceData) {
    const { data, error } = await supabase?.from('booth_resources')?.insert({
        institute_id: resourceData?.instituteId,
        resource_name: resourceData?.resourceName,
        resource_type: resourceData?.resourceType,
        file_url: resourceData?.fileUrl,
        file_size: resourceData?.fileSize,
        description: resourceData?.description,
        is_public: resourceData?.isPublic !== false
      })?.select()?.single();

    if (error) throw error;
    return data;
  },

  async deleteResource(resourceId) {
    const { error } = await supabase?.from('booth_resources')?.delete()?.eq('id', resourceId);

    if (error) throw error;
  },

  // ===== VISITOR ANALYTICS =====
  async trackBoothVisit(visitData) {
    return errorHandler?.handleAsync(async () => {
      // CRITICAL FIX: booth_visits.institute_id references users.id, not institute_profiles.id
      // We need to get the user_id from institute_profiles first
      
      // Validate the institute ID format FIRST
      if (!validationUtils?.isValidUUID(visitData?.instituteId)) {
        console.warn('Invalid institute ID format for visit tracking, skipping:', visitData?.instituteId);
        return null; // Silently fail for invalid IDs - don't throw error
      }
      
      // Try to get the user_id from institute_profiles
      const { data: instituteProfile, error: profileError } = await supabase
        ?.from('institute_profiles')
        ?.select('user_id')
        ?.eq('id', visitData?.instituteId)
        ?.single();
      
      // If we couldn't get the profile or user_id, abort the visit tracking
      if (profileError || !instituteProfile?.user_id) {
        console.warn('Could not map institute profile to user_id for visit tracking:', {
          instituteId: visitData?.instituteId,
          error: profileError?.message
        });
        return null; // Silently fail - don't disrupt user experience
      }
      
      let instituteUserId = instituteProfile?.user_id;
      
      // Validate the final institute user ID
      if (!validationUtils?.isValidUUID(instituteUserId)) {
        console.warn('Invalid institute user ID after mapping, skipping visit tracking:', instituteUserId);
        return null; // Silently fail
      }
      
      // Insert the visit record with validated IDs
      const { data, error } = await supabase?.from('booth_visits')?.insert({
          institute_id: instituteUserId,
          student_id: visitData?.studentId || null,
          visit_duration_seconds: visitData?.visitDurationSeconds || 0,
          pages_viewed: visitData?.pagesViewed || 1,
          actions_taken: visitData?.actionsTaken || [],
          referrer_url: visitData?.referrerUrl || null,
          device_type: visitData?.deviceType || 'desktop'
        })?.select()?.single();

      if (error) {
        // Log the error details for debugging but don't throw
        console.error('Error inserting booth visit (non-critical):', {
          error: error?.message,
          code: error?.code,
          instituteUserId,
          originalInstituteId: visitData?.instituteId,
          studentId: visitData?.studentId
        });
        return null; // Return null instead of throwing - visit tracking is non-critical
      }
      
      return data;
    }, 'trackBoothVisit');
  },

  async getBoothAnalytics(instituteId, days = 30) {
    const { data, error } = await supabase?.rpc('get_booth_analytics', {
        p_institute_id: instituteId,
        p_days: days
      });

    if (error) throw error;
    
    if (!data || data?.length === 0) {
      return {
        totalVisits: 0,
        uniqueVisitors: 0,
        avgDurationSeconds: 0,
        totalDownloads: 0,
        totalBookings: 0,
        avgRating: 0,
        totalReviews: 0
      };
    }

    return {
      totalVisits: parseInt(data?.[0]?.total_visits || 0),
      uniqueVisitors: parseInt(data?.[0]?.unique_visitors || 0),
      avgDurationSeconds: parseFloat(data?.[0]?.avg_duration_seconds || 0),
      totalDownloads: parseInt(data?.[0]?.total_downloads || 0),
      totalBookings: parseInt(data?.[0]?.total_bookings || 0),
      avgRating: parseFloat(data?.[0]?.avg_rating || 0),
      totalReviews: parseInt(data?.[0]?.total_reviews || 0)
    };
  },

  // ===== REAL-TIME SUBSCRIPTIONS =====
  subscribeToReviews(instituteId, callback) {
    const channel = supabase?.channel(`reviews:${instituteId}`)?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'booth_reviews',
          filter: `institute_id=eq.${instituteId}`
        },
        (payload) => callback(payload?.new)
      )?.subscribe();

    return () => supabase?.removeChannel(channel);
  },

  subscribeToBookings(userId, callback) {
    const channel = supabase?.channel(`bookings:${userId}`)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'counselor_bookings',
          filter: `student_id=eq.${userId}`
        },
        (payload) => callback(payload)
      )?.subscribe();

    return () => supabase?.removeChannel(channel);
  }
};

// Helper: format "09:00:00" start/end → "9:00 AM – 10:00 AM"
function formatSlotLabel(startTime, endTime) {
  const fmt = (time) => {
    if (!time) return '';
    const [hours, minutes] = time?.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };
  return `${fmt(startTime)} – ${fmt(endTime)}`;
}